import type { QueryCacheUserChangeEvent, QueryCacheUserServiceDataSource } from './index';
import { effect } from '@preact/signals-react';
import { createStructuredSelector } from 'reselect';
import { getCurrentIsSuperuserEnabledFlag, getUser } from 'src/store/selectors';
import type { IsomorphicBindings } from 'src/types/bindings';

const getEffectiveUserIdentity = createStructuredSelector({
    userId: (state) => getUser(state)?.id,
    isSuperuserEnabled: getCurrentIsSuperuserEnabledFlag,
});

export function createStoreCurrentUserQueryCacheUserServiceDataSource(
    store: IsomorphicBindings['Store'],
    currentUserModel: IsomorphicBindings['CurrentUserModel'],
): QueryCacheUserServiceDataSource {
    return {
        subscribe(callback) {
            const disposableStack = new DisposableStack();

            disposableStack.defer(
                store.subscribeSelector(getEffectiveUserIdentity).handle((identity) => {
                    callback({
                        kind: 'identity',
                        effectiveUserId: identity.userId,
                        fingerprint: JSON.stringify(identity),
                    } satisfies QueryCacheUserChangeEvent);
                }),
            );

            let previousEntitlementsRevision = currentUserModel.entitlementsRevision.peek();
            disposableStack.defer(
                effect(() => {
                    const nextEntitlementsRevision = currentUserModel.entitlementsRevision.value;
                    if (nextEntitlementsRevision === previousEntitlementsRevision) return;

                    previousEntitlementsRevision = nextEntitlementsRevision;
                    callback({
                        kind: 'entitlements',
                        effectiveUserId: currentUserModel.userId.peek(),
                        fingerprint: String(nextEntitlementsRevision),
                    } satisfies QueryCacheUserChangeEvent);
                }),
            );

            return () => disposableStack.dispose();
        },
    };
}
